import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import {
  Users2, Plus, CalendarClock, Rocket, Trash2, LifeBuoy,
  CheckCircle2, XCircle, CalendarOff, Gauge, ArrowLeftRight, Clock,
  Upload, Copy, Download, AlertTriangle,
} from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SHIFT_STATUS: Record<string, string> = {
  scheduled: "good", covered: "info", open: "warn", callout: "critical", cancelled: "inactive",
};

function mondayOf(d: Date): string {
  const day = (d.getDay() + 6) % 7; // 0=Mon
  const m = new Date(d);
  m.setDate(d.getDate() - day);
  return m.toISOString().slice(0, 10);
}
const parseList = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function Scheduling() {
  const {
    t
  } = useI18n();

  const { hasPerm, modules } = useAuth();
  const canManage = hasPerm("wf.manage");
  const [stores, setStores] = useState<any[]>([]);
  const [storeId, setStoreId] = useState<string>("");
  const [week, setWeek] = useState<string>(mondayOf(new Date()));
  const [covDate, setCovDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [tab, setTab] = useState("roster");

  useEffect(() => {
    api.get("/org/stores").then((r) => {
      setStores(r.data.stores);
      if (r.data.stores[0]) setStoreId(r.data.stores[0].id);
    }).catch(() => {});
  }, []);

  if (modules.workforce === false) {
    return (
      <div>
        <PageHeader title="Workforce & Scheduling" testId="scheduling-header" />
        <EmptyState icon={<Users2 size={28} />} title="Workforce module is inactive"
          description="Enable the Workforce module in Admin → Modules to use scheduling." />
      </div>
    );
  }

  return (
    (<div>
      <PageHeader title="Workforce & Scheduling"
        subtitle="Roster, availability, weekly schedules, open shifts, swaps, callouts and coverage"
        testId="scheduling-header" />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">{t("Store")}</Label>
          <Select value={storeId} onValueChange={setStoreId}>
            <SelectTrigger className="mt-1 w-64" data-testid="sched-store-select"><SelectValue placeholder={t("Select store")} /></SelectTrigger>
            <SelectContent>
              {stores.map((s) => <SelectItem key={s.id} value={s.id}>{t("Store")}{" "}{s.store_number}· {s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {tab === "schedule" && (
          <div>
            <Label className="text-xs">{t("Week of (Mon)")}</Label>
            <Input type="date" className="mt-1 w-44" value={week} onChange={(e) => setWeek(e.target.value)} data-testid="sched-week-input" />
          </div>
        )}
        {tab === "coverage" && (
          <div>
            <Label className="text-xs">{t("Date")}</Label>
            <Input type="date" className="mt-1 w-44" value={covDate} onChange={(e) => setCovDate(e.target.value)} data-testid="sched-covdate-input" />
          </div>
        )}
      </div>
      {!storeId ? <LoadingBlock /> : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="roster" data-testid="sched-tab-roster">{t("Roster")}</TabsTrigger>
            <TabsTrigger value="schedule" data-testid="sched-tab-schedule">{t("Schedule")}</TabsTrigger>
            {canManage && <TabsTrigger value="requests" data-testid="sched-tab-requests">{t("Requests")}</TabsTrigger>}
            <TabsTrigger value="coverage" data-testid="sched-tab-coverage">{t("Coverage")}</TabsTrigger>
          </TabsList>

          <TabsContent value="roster"><RosterPanel storeId={storeId} canManage={canManage} /></TabsContent>
          <TabsContent value="schedule"><SchedulePanel storeId={storeId} week={week} canManage={canManage} /></TabsContent>
          {canManage && <TabsContent value="requests"><RequestsPanel storeId={storeId} /></TabsContent>}
          <TabsContent value="coverage"><CoverageTab storeId={storeId} date={covDate} /></TabsContent>
        </Tabs>
      )}
    </div>)
  );
}

/* ------------------------- ROSTER ------------------------- */
const RosterPanel: React.FC<{ storeId: string; canManage: boolean }> = ({ storeId, canManage }) => {
  const {
    t
  } = useI18n();

  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [avFor, setAvFor] = useState<any>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/scheduling/profiles?store_id=${storeId}`).then((r) => setProfiles(r.data.profiles)).finally(() => setLoading(false));
  }, [storeId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingBlock />;
  const exportRoster = async () => {
    try {
      const r = await api.get(`/scheduling/roster/export?store_id=${storeId}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = "roster.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  return (
    (<div>
      {canManage && <div className="mb-3 flex gap-2">
        <Button onClick={() => setOpen(true)} data-testid="roster-add-btn"><Plus size={16} className="mr-1.5" />{t("Add employee")}</Button>
        <Button variant="outline" onClick={exportRoster} data-testid="roster-export-btn"><Download size={16} className="mr-1.5" />{t("Export roster")}</Button>
      </div>}
      {profiles.length === 0 ? (
        <EmptyState icon={<Users2 size={28} />} title="No employees on this store's roster"
          description={canManage ? t("Add employees to start building schedules.") : t("Roster entries will appear here.")} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="roster-list">
          {profiles.map((p) => (
            <Card key={p.id} className="p-4" data-testid={`roster-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="font-display font-semibold">{p.display_name}</div>
                <StatusPill status={p.status === "active" ? "good" : p.status === "on_leave" ? "warn" : "inactive"}>{p.status.replace("_", " ")}</StatusPill>
              </div>
              <div className="mt-1 text-xs capitalize text-muted-foreground">{p.employment_type.replace("_", " ")}{p.user_id ? t("· linked") : ""}</div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(p.roles || []).map((r: string) => <span key={r} className="rounded bg-secondary px-2 py-0.5 text-[11px]">{r}</span>)}
                {(p.skills || []).map((s: string) => <span key={s} className="rounded bg-accent px-2 py-0.5 text-[11px] text-accent-foreground">{s}</span>)}
              </div>
              {canManage && <Button variant="outline" size="sm" className="mt-3" onClick={() => setAvFor(p)} data-testid={`roster-availability-${p.id}`}><Clock size={13} className="mr-1" />{t("Availability")}</Button>}
            </Card>
          ))}
        </div>
      )}
      {canManage && <AddProfileDialog open={open} setOpen={setOpen} storeId={storeId} onDone={load} />}
      {avFor && <AvailabilityDialog profile={avFor} onClose={() => setAvFor(null)} />}
    </div>)
  );
};

const AddProfileDialog: React.FC<{ open: boolean; setOpen: (v: boolean) => void; storeId: string; onDone: () => void }> = ({ open, setOpen, storeId, onDone }) => {
  const {
    t
  } = useI18n();

  const [name, setName] = useState("");
  const [etype, setEtype] = useState("part_time");
  const [roles, setRoles] = useState("");
  const [skills, setSkills] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.post("/scheduling/profiles", {
        display_name: name.trim(), employment_type: etype, primary_store_id: storeId,
        store_ids: [storeId], roles: parseList(roles), skills: parseList(skills),
      });
      toast.success("Employee added"); setOpen(false); setName(""); setRoles(""); setSkills(""); onDone();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    (<Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="roster-add-dialog">
        <DialogHeader><DialogTitle>{t("Add employee")}</DialogTitle><DialogDescription>{t("Create a roster entry for this store.")}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>{t("Name")}</Label><Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" /></div>
          <div>
            <Label>{t("Employment type")}</Label>
            <Select value={etype} onValueChange={setEtype}>
              <SelectTrigger className="mt-1" data-testid="profile-etype"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full_time">{t("Full-time")}</SelectItem><SelectItem value="part_time">{t("Part-time")}</SelectItem>
                <SelectItem value="temporary">{t("Temporary")}</SelectItem><SelectItem value="seasonal">{t("Seasonal")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>{t("Roles (comma separated)")}</Label><Input className="mt-1" placeholder={t("cashier, lead")} value={roles} onChange={(e) => setRoles(e.target.value)} data-testid="profile-roles" /></div>
          <div><Label>{t("Skills (comma separated)")}</Label><Input className="mt-1" placeholder={t("opening, cash_handling")} value={skills} onChange={(e) => setSkills(e.target.value)} data-testid="profile-skills" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid="profile-save">{t("Add employee")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>)
  );
};

const AvailabilityDialog: React.FC<{ profile: any; onClose: () => void }> = ({ profile, onClose }) => {
  const {
    t
  } = useI18n();

  const [rows, setRows] = useState<any[]>(DAYS.map((_, i) => ({ weekday: i, enabled: false, start: "09:00", end: "17:00" })));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/scheduling/profiles/${profile.id}`).then((r) => {
      const weekly = r.data.availability?.weekly || [];
      setRows(DAYS.map((_, i) => {
        const w = weekly.find((x: any) => x.weekday === i);
        return w ? { weekday: i, enabled: true, start: w.start, end: w.end } : { weekday: i, enabled: false, start: "09:00", end: "17:00" };
      }));
    }).catch(() => {});
  }, [profile.id]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/scheduling/profiles/${profile.id}/availability`, {
        weekly: rows.filter((r) => r.enabled).map((r) => ({ weekday: r.weekday, start: r.start, end: r.end })),
        exceptions: [],
      });
      toast.success("Availability saved"); onClose();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    (<Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="availability-dialog">
        <DialogHeader><DialogTitle>{profile.display_name}{" "}{t("· Availability")}</DialogTitle>
          <DialogDescription>{t(
            "Recurring weekly availability windows used to validate shift assignments."
          )}</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`avail-row-${i}`}>
              <label className="flex w-24 items-center gap-2 text-sm">
                <input type="checkbox" checked={r.enabled} onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x))} data-testid={`avail-enable-${i}`} />
                {DAYS[i]}
              </label>
              <Input type="time" className="w-32" value={r.start} disabled={!r.enabled} onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, start: e.target.value } : x))} />
              <span>–</span>
              <Input type="time" className="w-32" value={r.end} disabled={!r.enabled} onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, end: e.target.value } : x))} />
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid="avail-save">{t("Save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>)
  );
};

/* ------------------------- SCHEDULE ------------------------- */
const BAND_TONE: Record<string, string> = {
  under: "warn", over: "critical", on_target: "good", no_labor_plan: "inactive", closed: "inactive", no_demand: "inactive",
};

const QualityCard: React.FC<{ scheduleId: string }> = ({ scheduleId }) => {
  const { t } = useI18n();
  const [q, setQ] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    api.get(`/scheduling/schedules/${scheduleId}/quality`).then((r) => setQ(r.data)).catch(() => setQ(null));
  }, [scheduleId]);
  useEffect(() => { load(); }, [load]);
  if (!q) return null;
  return (
    <Card className="mb-3 p-3" data-testid="schedule-quality-card">
      <button className="flex w-full items-center justify-between" onClick={() => setOpen((v) => !v)} data-testid="schedule-quality-toggle">
        <div className="flex items-center gap-2">
          <Gauge size={16} className="text-brand" />
          <span className="text-sm font-medium">{t("quality.title")}</span>
          {!q.labor_plan.configured && <StatusPill status="inactive" testId="quality-not-configured">{t("common.not_configured")}</StatusPill>}
        </div>
        <span className="text-xs text-muted-foreground" data-testid="schedule-quality-summary">
          {t("quality.scheduled")} {q.totals.scheduled_hours}h{q.totals.required_hours != null ? ` · ${t("quality.planned")} ${q.totals.required_hours}h` : ""} · {t("quality.gaps")} {q.totals.coverage_gaps}
        </span>
      </button>
      <p className="mt-1 text-xs text-muted-foreground">{t("quality.subtitle")}</p>
      {open && (
        <div className="mt-3 space-y-2" data-testid="schedule-quality-detail">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {q.days.map((d: any) => (
              <div key={d.date} className="rounded-md border border-border p-2" data-testid={`quality-day-${d.date}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-muted-foreground">{d.date.slice(5)}</span>
                  <StatusPill status={BAND_TONE[d.band] || "info"}>{t(`quality.band.${d.band}`)}</StatusPill>
                </div>
                <div className="mt-1 text-xs">{d.scheduled_hours}h{d.required_hours != null ? ` / ${d.required_hours}h` : ""}</div>
                {d.coverage_gaps > 0 && <div className="text-[11px] text-status-warn">{d.coverage_gaps} {t("quality.gaps").toLowerCase()}</div>}
              </div>
            ))}
          </div>
          {q.recommendations.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-muted-foreground">{t("quality.recommendations")}</div>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-muted-foreground" data-testid="quality-recommendations">
                {q.recommendations.map((r: string, i: number) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const SchedulePanel: React.FC<{ storeId: string; week: string; canManage: boolean }> = ({ storeId, week, canManage }) => {
  const {
    t
  } = useI18n();

  const [schedule, setSchedule] = useState<any>(null);
  const [shifts, setShifts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [coverFor, setCoverFor] = useState<any>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [budget, setBudget] = useState<string>("");
  const [hoursByDate, setHoursByDate] = useState<Record<string, any>>({});
  const fileRef = React.useRef<HTMLInputElement | null>(null);

  const weekStart = useMemo(() => mondayOf(new Date(week + "T00:00:00")), [week]);
  const weekEnd = useMemo(() => { const d = new Date(weekStart + "T00:00:00"); d.setDate(d.getDate() + 6); return d.toISOString().slice(0, 10); }, [weekStart]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/scheduling/schedules?store_id=${storeId}&week_start=${weekStart}`)
      .then((r) => { setSchedule(r.data.schedule); setShifts(r.data.shifts); setBudget(r.data.schedule?.labor_hour_budget != null ? String(r.data.schedule.labor_hour_budget) : ""); })
      .finally(() => setLoading(false));
    api.get(`/calendar/effective?store_id=${storeId}&start=${weekStart}&end=${weekEnd}`)
      .then((r) => { const m: Record<string, any> = {}; (r.data.days || []).forEach((d: any) => { m[d.date] = d; }); setHoursByDate(m); })
      .catch(() => setHoursByDate({}));
  }, [storeId, weekStart, weekEnd]);
  useEffect(() => { load(); }, [load]);

  const createSchedule = async () => {
    try { await api.post("/scheduling/schedules", { store_id: storeId, week_start: weekStart }); toast.success("Draft schedule created"); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const publish = async () => {
    try { const r = await api.post(`/scheduling/schedules/${schedule.id}/publish`, {}); toast.success(tl("Published v{v} · {shifts} shifts · {notified} notified", { v: r.data.version_no, shifts: r.data.shift_count, notified: r.data.notified })); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Publish failed"); }
  };
  const saveBudget = async () => {
    try { await api.post(`/scheduling/schedules/${schedule.id}/budget`, { labor_hour_budget: budget === "" ? null : Number(budget) }); toast.success("Budget saved"); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const importCsv = async (file: File) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/scheduling/schedules/${schedule.id}/import?import_id=${Date.now()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(tl("Imported {n} shifts", { n: r.data.imported })); load();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      if (d?.errors) toast.error(`${d.message}: ${d.errors.slice(0, 3).join(" | ")}`);
      else toast.error(d || "Import failed");
    }
  };
  const exportCsv = async () => {
    try {
      const r = await api.get(`/scheduling/schedules/${schedule.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `schedule_${weekStart}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const act = async (fn: () => Promise<any>, ok: string) => {
    try { await fn(); toast.success(ok); load(); } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <LoadingBlock />;
  if (!schedule) {
    return (
      (<div>
        {canManage && <Button className="mb-4" onClick={createSchedule} data-testid="schedule-create-btn"><Plus size={16} className="mr-1.5" />{t("Create draft schedule")}</Button>}
        <EmptyState icon={<CalendarClock size={28} />} title={tl("No schedule for week of {week}", { week: weekStart })}
          description={canManage ? t(
            "Create a draft schedule and add shifts, then publish an immutable version."
          ) : t("Published schedules appear here.")} />
      </div>)
    );
  }

  const byDay: Record<string, any[]> = {};
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart + "T00:00:00"); d.setDate(d.getDate() + i);
    byDay[d.toISOString().slice(0, 10)] = [];
  }
  shifts.forEach((s) => { if (byDay[s.date]) byDay[s.date].push(s); });

  return (
    (<div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <StatusPill status={schedule.status === "published" ? "good" : schedule.status === "draft" ? "info" : "inactive"} testId="schedule-status">{stat(schedule.status)}</StatusPill>
          {schedule.current_version_no > 0 && <span className="text-muted-foreground">v{schedule.current_version_no}</span>}
          {schedule.dirty && <span className="text-xs text-status-warn" data-testid="schedule-dirty">{t("unpublished changes")}</span>}
          <span className="text-muted-foreground" data-testid="schedule-hours">{schedule.scheduled_hours}{t("h scheduled")}</span>
          {schedule.budget_variance != null && (
            <span className={schedule.budget_variance > 0 ? t("text-status-critical") : t("text-status-good")} data-testid="schedule-variance">
              {schedule.budget_variance > 0 ? "+" : ""}{schedule.budget_variance}{t("h vs budget")}</span>
          )}
        </div>
        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <Input type="number" className="w-24" placeholder={t("Budget h")} value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="schedule-budget-input" />
              <Button variant="ghost" size="sm" onClick={saveBudget} data-testid="schedule-budget-save">{t("Set")}</Button>
            </div>
            <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} data-testid="shift-add-btn"><Plus size={15} className="mr-1" />{t("Add shift")}</Button>
            <input type="file" accept=".csv" hidden ref={fileRef} onChange={(e) => { const f = e.target.files?.[0]; if (f) importCsv(f); e.target.value = ""; }} data-testid="schedule-import-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="schedule-import-btn"><Upload size={15} className="mr-1" />{t("Import")}</Button>
            <Button variant="outline" size="sm" onClick={() => setCopyOpen(true)} data-testid="schedule-copy-btn"><Copy size={15} className="mr-1" />{t("Copy week")}</Button>
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="schedule-export-btn"><Download size={15} className="mr-1" />{t("Export")}</Button>
            <Button size="sm" onClick={publish} data-testid="schedule-publish-btn"><Rocket size={15} className="mr-1" />{schedule.status === "published" ? t("Publish revision") : t("Publish")}</Button>
          </div>
        )}
      </div>
      {canManage && <QualityCard scheduleId={schedule.id} />}
      {schedule.hours_revision_needed && (
        <Card className="mb-3 border-status-warn/50 bg-status-warn/10 p-3" data-testid="schedule-hours-revision">
          <div className="flex items-center gap-2 text-sm text-status-warn">
            <AlertTriangle size={15} />{t(
            "Operating hours changed after this schedule was published. Review affected shifts and publish an explicit revision — shifts are never silently rewritten."
          )}</div>
        </Card>
      )}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {Object.entries(byDay).map(([d, list], idx) => {
          const h = hoursByDate[d];
          return (
            (<Card key={d} className="p-3" data-testid={`sched-day-${d}`}>
              <div className="mb-1 flex items-center justify-between">
                <div className="text-xs font-semibold uppercase text-muted-foreground">{DAYS[idx]} · {d.slice(5)}</div>
                {h?.configured && (h.closed
                  ? <span className="text-[11px] text-status-critical" data-testid={`sched-day-hours-${d}`}>{t("Closed")}{h.label ? ` · ${h.label}` : ""}</span>
                  : <span className="text-[11px] text-muted-foreground" data-testid={`sched-day-hours-${d}`}>{h.store_open}–{h.store_close}</span>)}
              </div>
              {list.length === 0 ? <div className="text-xs text-muted-foreground">{t("No shifts")}</div> : (
                <div className="space-y-2">
                  {list.sort((a, b) => a.start.localeCompare(b.start)).map((s) => (
                    <div key={s.id} className="rounded-md border border-border p-2" data-testid={`shift-${s.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{s.start}–{s.end}</span>
                        <StatusPill status={SHIFT_STATUS[s.status] || "info"}>{stat(s.status)}</StatusPill>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {s.role || tl("any role")} · {s.assigned_name || (s.status === "open" ? tl("OPEN") : tl("unassigned"))}
                      </div>
                      {s.callout?.reason && <div className="mt-0.5 text-[11px] text-status-critical">{t("callout:")}{" "}{s.callout.reason}</div>}
                      {canManage && s.status !== "cancelled" && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {(s.status === "scheduled" || s.status === "covered") && (
                            <>
                              <button className="text-[11px] text-brand hover:underline" onClick={() => act(() => api.post(`/scheduling/shifts/${s.id}/callout`, { reason: "Called out" }), "Marked callout")} data-testid={`shift-callout-${s.id}`}>{t("Callout")}</button>
                              <button className="text-[11px] text-muted-foreground hover:underline" onClick={() => act(() => api.post(`/scheduling/shifts/${s.id}/open`, {}), "Shift opened")} data-testid={`shift-open-${s.id}`}>{t("Open")}</button>
                            </>
                          )}
                          {(s.status === "callout" || s.status === "open") && (
                            <button className="text-[11px] text-status-good hover:underline" onClick={() => setCoverFor(s)} data-testid={`shift-cover-${s.id}`}>{t("Cover")}</button>
                          )}
                          {schedule.status === "draft" && (
                            <button className="text-[11px] text-status-critical hover:underline" onClick={() => act(() => api.delete(`/scheduling/shifts/${s.id}`), "Shift removed")} data-testid={`shift-delete-${s.id}`}><Trash2 size={11} className="inline" /></button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>)
          );
        })}
      </div>
      {canManage && addOpen && <AddShiftDialog schedule={schedule} weekStart={weekStart} storeId={storeId} hoursByDate={hoursByDate} onClose={() => setAddOpen(false)} onDone={load} />}
      {coverFor && <CoverDialog shift={coverFor} onClose={() => setCoverFor(null)} onDone={load} />}
      {copyOpen && <CopyForwardDialog scheduleId={schedule.id} weekStart={weekStart} onClose={() => setCopyOpen(false)} onDone={load} />}
    </div>)
  );
};

const CopyForwardDialog: React.FC<{ scheduleId: string; weekStart: string; onClose: () => void; onDone: () => void }> = ({ scheduleId, weekStart, onClose, onDone }) => {
  const {
    t
  } = useI18n();

  const prevMonday = new Date(weekStart + "T00:00:00");prevMonday.setDate(prevMonday.getDate() - 7);
  const [src, setSrc] = useState(prevMonday.toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/scheduling/schedules/${scheduleId}/copy-forward`, { source_week_start: mondayOf(new Date(src + "T00:00:00")) });
      const opened = r.data.opened_due_to_conflict?.length || 0;
      toast.success(`${tl("Copied {n} shifts", { n: r.data.copied })}${opened ? tl(" · {n} opened due to conflicts", { n: opened }) : ""}`);
      onDone(); onClose();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    (<Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="copy-forward-dialog">
        <DialogHeader><DialogTitle>{t("Copy a week forward")}</DialogTitle>
          <DialogDescription>{t(
            "Shifts are re-validated (eligibility, availability, time-off, overlap, role/skill, store scope). Conflicting assignments are copied as open shifts."
          )}</DialogDescription></DialogHeader>
        <div><Label>{t("Source week (Mon)")}</Label><Input type="date" className="mt-1" value={src} onChange={(e) => setSrc(e.target.value)} data-testid="copy-source-week" /></div>
        <DialogFooter><Button onClick={run} disabled={busy} data-testid="copy-forward-confirm">{t("Copy forward")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>)
  );
};

const AddShiftDialog: React.FC<{ schedule: any; weekStart: string; storeId: string; hoursByDate: Record<string, any>; onClose: () => void; onDone: () => void }> = ({ schedule, weekStart, storeId, hoursByDate, onClose, onDone }) => {
  const {
    t
  } = useI18n();

  const [date, setDate] = useState(weekStart);
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("17:00");
  const [role, setRole] = useState("");
  const [assign, setAssign] = useState("open");
  const [profiles, setProfiles] = useState<any[]>([]);
  const [overrideReason, setOverrideReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/scheduling/profiles?store_id=${storeId}&status=active`).then((r) => setProfiles(r.data.profiles)).catch(() => {}); }, [storeId]);

  const h = hoursByDate[date];
  const toMin = (v: string) => { const [hh, mm] = v.split(":").map(Number); return hh * 60 + mm; };
  const closed = !!(h?.configured && h?.closed);
  const outside = !!(h?.configured && !h.closed && h.window_open && h.window_close && (toMin(start) < toMin(h.window_open) || toMin(end) > toMin(h.window_close)));
  const needReason = closed || outside;

  const save = async () => {
    if (needReason && !overrideReason.trim()) { toast.error("An override reason is required for this shift"); return; }
    setBusy(true);
    try {
      await api.post(`/scheduling/schedules/${schedule.id}/shifts`, {
        date, start, end, role: role.trim() || null, required_skills: [],
        assigned_profile_id: assign === "open" ? null : assign, is_open: assign === "open", break_minutes: 30,
        override_reason: needReason ? overrideReason.trim() : null, emergency_override: closed,
      });
      toast.success("Shift added"); onDone(); onClose();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    (<Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="add-shift-dialog">
        <DialogHeader><DialogTitle>{t("Add shift")}</DialogTitle><DialogDescription>{t(
          "Validated against operating hours, role, availability, time-off and overlaps."
        )}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div><Label>{t("Date")}</Label><Input type="date" className="mt-1" value={date} onChange={(e) => setDate(e.target.value)} data-testid="shift-date" /></div>
            <div><Label>{t("Start")}</Label><Input type="time" className="mt-1" value={start} onChange={(e) => setStart(e.target.value)} data-testid="shift-start" /></div>
            <div><Label>{t("End")}</Label><Input type="time" className="mt-1" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="shift-end" /></div>
          </div>
          {h?.configured && (
            <div className="rounded-md border border-border bg-muted/30 p-2 text-xs" data-testid="add-shift-hours">
              {closed
                ? <span className="text-status-critical">{t("Store is CLOSED on")}{" "}{date}{h.label ? ` (${h.label})` : ""}{t(". Staffing requires an emergency override + reason.")}</span>
                : <span className="text-muted-foreground">{t("Permitted staffing window:")}{" "}{h.window_open}–{h.window_close}{" "}{t("(store")}{" "}{h.store_open}–{h.store_close})</span>}
            </div>
          )}
          <div><Label>{t("Role")}</Label><Input className="mt-1" placeholder={t("cashier")} value={role} onChange={(e) => setRole(e.target.value)} data-testid="shift-role" /></div>
          <div>
            <Label>{t("Assign to")}</Label>
            <Select value={assign} onValueChange={setAssign}>
              <SelectTrigger className="mt-1" data-testid="shift-assign"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">{t("Leave open (post as open shift)")}</SelectItem>
                {profiles.map((p) => <SelectItem key={p.id} value={p.id}>{p.display_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {needReason && (
            <div data-testid="add-shift-override">
              <Label className="text-status-warn">{closed ? t("Emergency-override reason") : t("Out-of-window override reason")}</Label>
              <Input className="mt-1" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder={t("Auditable reason")} data-testid="shift-override-reason" />
            </div>
          )}
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid="shift-save">{t("Add shift")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>)
  );
};

const CoverDialog: React.FC<{ shift: any; onClose: () => void; onDone: () => void }> = ({ shift, onClose, onDone }) => {
  const {
    t
  } = useI18n();

  const [candidates, setCandidates] = useState<any[]>([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/scheduling/shifts/${shift.id}/eligible`).then((r) => setCandidates(r.data.candidates)).catch(() => {}); }, [shift.id]);
  const cover = async () => {
    if (!pick) { toast.error("Pick an eligible employee"); return; }
    setBusy(true);
    try { await api.post(`/scheduling/shifts/${shift.id}/cover`, { profile_id: pick }); toast.success("Coverage assigned"); onDone(); onClose(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    (<Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="cover-dialog">
        <DialogHeader><DialogTitle>{t("Redeploy coverage ·")}{" "}{shift.start}–{shift.end}</DialogTitle>
          <DialogDescription>{t("Only eligible employees (role, availability, no conflicts) are shown.")}</DialogDescription></DialogHeader>
        {candidates.length === 0 ? <EmptyState title="No eligible employees" description="Adjust availability or roster to redeploy." /> : (
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger data-testid="cover-select"><SelectValue placeholder={t("Choose employee")} /></SelectTrigger>
            <SelectContent>{candidates.map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <DialogFooter><Button onClick={cover} disabled={busy || !pick} data-testid="cover-confirm">{t("Assign coverage")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>)
  );
};

/* ------------------------- REQUESTS ------------------------- */
const RequestsPanel: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t: tI18n
  } = useI18n();

  const [claims, setClaims] = useState<any[]>([]);
  const [swaps, setSwaps] = useState<any[]>([]);
  const [timeoff, setTimeoff] = useState<any[]>([]);
  const load = useCallback(() => {
    Promise.all([
      api.get("/scheduling/claims?status=pending"),
      api.get("/scheduling/swaps"),
      api.get("/scheduling/timeoff?status=pending"),
    ]).then(([c, s, t]) => { setClaims(c.data.claims); setSwaps(s.data.swaps); setTimeoff(t.data.timeoff); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (fn: () => Promise<any>, ok: string) => {
    try { await fn(); toast.success(ok); load(); } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    (<div className="space-y-6" data-testid="requests-panel">
      <div>
        <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold"><LifeBuoy size={16} className="text-brand" />{tI18n("Open-shift claims (")}{claims.length})</div>
        {claims.length === 0 ? <div className="text-sm text-muted-foreground">{tI18n("No pending claims.")}</div> : (
          <div className="space-y-2">
            {claims.map((c) => (
              <Card key={c.id} className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid={`claim-${c.id}`}>
                <div className="text-sm"><span className="font-medium">{c.claimant_name}</span> · {c.shift?.date} {c.shift?.start}–{c.shift?.end} ({c.shift?.role || "any"})</div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => decide(() => api.post(`/scheduling/claims/${c.id}/decision`, { approve: true }), "Claim approved")} data-testid={`claim-approve-${c.id}`}><CheckCircle2 size={14} className="mr-1" />{tI18n("Approve")}</Button>
                  <Button size="sm" variant="outline" onClick={() => decide(() => api.post(`/scheduling/claims/${c.id}/decision`, { approve: false, note: "declined" }), "Claim denied")} data-testid={`claim-deny-${c.id}`}><XCircle size={14} /></Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold"><ArrowLeftRight size={16} className="text-status-info" />{tI18n("Swap requests (")}{swaps.length})</div>
        {swaps.length === 0 ? <div className="text-sm text-muted-foreground">{tI18n("No swaps.")}</div> : (
          <div className="space-y-2">
            {swaps.map((s) => (
              <Card key={s.id} className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid={`swap-${s.id}`}>
                <div className="text-sm">
                  <span className="font-medium">{s.requester_name}</span> → {s.accepted_name || "awaiting acceptance"}
                  <span className="text-muted-foreground"> · {s.shift?.date} {s.shift?.start}–{s.shift?.end}</span>
                  <StatusPill status={s.status === "accepted" ? "warn" : s.status === "approved" ? "good" : "info"}>{stat(s.status)}</StatusPill>
                </div>
                {s.status === "accepted" && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => decide(() => api.post(`/scheduling/swaps/${s.id}/decision`, { approve: true }), "Swap approved")} data-testid={`swap-approve-${s.id}`}><CheckCircle2 size={14} className="mr-1" />{tI18n("Approve")}</Button>
                    <Button size="sm" variant="outline" onClick={() => decide(() => api.post(`/scheduling/swaps/${s.id}/decision`, { approve: false, note: "declined" }), "Swap denied")} data-testid={`swap-deny-${s.id}`}><XCircle size={14} /></Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold"><CalendarOff size={16} className="text-status-warn" />{tI18n("Time-off requests (")}{timeoff.length})</div>
        {timeoff.length === 0 ? <div className="text-sm text-muted-foreground">{tI18n("No pending time-off.")}</div> : (
          <div className="space-y-2">
            {timeoff.map((t) => (
              <Card key={t.id} className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid={`timeoff-${t.id}`}>
                <div className="text-sm"><span className="font-medium">{t.profile_name}</span> · {t.start_date} → {t.end_date}{t.reason ? ` · ${t.reason}` : ""}</div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => decide(() => api.post(`/scheduling/timeoff/${t.id}/decision`, { approve: true }), "Time-off approved")} data-testid={`timeoff-approve-${t.id}`}><CheckCircle2 size={14} className="mr-1" />{tI18n("Approve")}</Button>
                  <Button size="sm" variant="outline" onClick={() => decide(() => api.post(`/scheduling/timeoff/${t.id}/decision`, { approve: false, note: "denied" }), "Time-off denied")} data-testid={`timeoff-deny-${t.id}`}><XCircle size={14} /></Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>)
  );
};

/* ------------------------- COVERAGE ------------------------- */
export const CoverageTab: React.FC<{ storeId: string; date: string }> = ({ storeId, date }) => {
  const {
    t
  } = useI18n();

  const [cov, setCov] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/scheduling/coverage?store_id=${storeId}&date=${date}`).then((r) => setCov(r.data.coverage)).finally(() => setLoading(false));
  }, [storeId, date]);
  if (loading) return <LoadingBlock />;
  if (!cov?.has_schedule) return <EmptyState icon={<Gauge size={28} />} title="No published schedule" description={tl("No coverage data for {date}.", { date })} />;
  const metrics = [
    { k: "Planned", v: cov.planned_shifts }, { k: "Filled", v: cov.filled_shifts },
    { k: "Open", v: cov.open_shifts }, { k: "Callouts", v: cov.callouts },
  ];
  return (
    (<div data-testid="coverage-tab">
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div className="font-display font-semibold">{t("Coverage ·")}{" "}{cov.date}</div>
          <StatusPill status={cov.coverage_pct >= 90 ? "good" : cov.coverage_pct >= 70 ? "warn" : "critical"} testId="coverage-pct">{cov.coverage_pct}{t("% covered")}</StatusPill>
        </div>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-secondary">
          <div className={`h-full rounded-full ${cov.coverage_pct >= 90 ? "bg-status-good" : cov.coverage_pct >= 70 ? "bg-status-warn" : "bg-status-critical"}`} style={{ width: `${cov.coverage_pct}%` }} />
        </div>
        <div className="mt-4 grid grid-cols-4 gap-2 text-center text-xs">
          {metrics.map((m) => (
            <div key={m.k} className="rounded-md border border-border p-2"><div className="font-display text-lg font-bold">{m.v}</div><div className="text-muted-foreground">{m.k}</div></div>
          ))}
        </div>
        <div className="mt-2 text-[11px] text-muted-foreground">{cov.filled_hours}{t("h filled of")}{" "}{cov.planned_hours}{t("h planned")}</div>
      </Card>
      {cov.gap_list.length > 0 && (
        <div className="mt-4">
          <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{t("Coverage gaps (")}{cov.coverage_gaps})</div>
          <div className="space-y-2" data-testid="coverage-gaps">
            {cov.gap_list.map((g: any) => (
              <Card key={g.shift_id} className="flex items-center justify-between p-3">
                <div className="text-sm">{g.start}–{g.end} · {g.role || "any"}{g.reason ? ` · ${g.reason}` : ""}</div>
                <StatusPill status={g.status === "callout" ? "critical" : "warn"}>{stat(g.status)}</StatusPill>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>)
  );
};
